import { Unit } from '../../../domain/usecases/Unit'
import { UnitType } from '../../../domain/models/UnitModel'
import { HttpClient } from '../../protocols/http/httpClient'
import { throwIfHttpError } from '../../../validation/httpError'

const CREATE_UNIT_ERROR = 'Não foi possível cadastrar a Unidade.'

/** Erro do createMany: diz quais unidades foram criadas antes da falha. */
export type CreateManyUnitsError = Error & {
  createdNames: string[]
  failedNames: string[]
}

export class RemoteCreateUnit implements Unit {
  constructor(
    private readonly url: string,
    private readonly httpClient: HttpClient
  ) {}

  async create(params: Unit.UnitParams): Promise<UnitType> {
    const { file, parking_slots = [], ...properties } = params

    const formData = new FormData()

    for (let i = 0; i < parking_slots.length; i++) {
      formData.append(`parking_slots[${i}]`, parking_slots[i])
    }
    Object.entries(properties).map((item: any) => {
      if (item[1]) {
        formData.append(item[0], item[1])
      }
    })
    formData.append('plan', params.file!)

    const response = await this.httpClient.post?.({
      url: `${this.url}/units`,
      data: formData,
      headers: {
        Accept: '*/*'
      }
    })
    return throwIfHttpError(response, CREATE_UNIT_ERROR)
  }

  async createMany(params: Unit.UnitParamsToCreateMany): Promise<void> {
    const { unitsName, parkingSlotByUnit, ...properties } = params

    // the backend doesn't support creating multiples yet.

    // forEach(async) resolvia antes de as requisições terminarem.
    // allSettled: com várias unidades, umas podem ser criadas e outras não;
    // o erro diz quais, para a tela não repetir as que já existem.
    const names = unitsName || []
    const results = await Promise.allSettled(
      names.map((name) =>
        this.create({
          name,
          parking_slots: parkingSlotByUnit?.[name],
          ...properties
        })
      )
    )
    const failedNames = names.filter((_, i) => results[i].status === 'rejected')
    if (!failedNames.length) return

    const createdNames = names.filter(
      (_, i) => results[i].status === 'fulfilled'
    )
    const reasons = Array.from(
      new Set(
        results
          .map((r) => (r.status === 'rejected' ? r.reason?.message : ''))
          .filter((message) => message && message !== CREATE_UNIT_ERROR)
      )
    )
    const detail = reasons.join(' / ')
    let message = `Não foi possível cadastrar ${
      failedNames.length > 1 ? 'as Unidades' : 'a Unidade'
    } ${failedNames.join(', ')}${detail ? `: ${detail}` : ''}`
    if (!/[.!?]$/.test(message)) message += '.'
    if (createdNames.length) {
      message +=
        createdNames.length > 1
          ? ` As Unidades ${createdNames.join(', ')} foram cadastradas.`
          : ` A Unidade ${createdNames[0]} foi cadastrada.`
    }
    const error = new Error(message) as CreateManyUnitsError
    error.createdNames = createdNames
    error.failedNames = failedNames
    throw error
  }

  async uploadUnitPhoto(params: Unit.UploadUnitPhoto): Promise<any> {
    const httpResponse = await this.httpClient.post?.({
      url: `${this.url}/units/${params.id}/upload-photo`,
      data: { base64: params.base64 }
    })

    if (httpResponse?.statusCode && httpResponse?.statusCode !== 201)
      throw new Error('error on upload unit photo')
    return httpResponse
  }
}
